import {jwtDecode as _decode} from 'lib/jwtDecode';
import Cookies from 'lib/cookies';

import notifier from '@albert-io/notifier';
import {isInternal, getEnvironment, getHost} from '@albert-io/environment';

import {IntercomService} from 'vendor/Intercom/browser/service';
import {callAction} from 'client/framework';
import SessionLifecycleActions from 'client/Session/SessionLifecycleActions';
import {clearMasqueradeState} from 'client/generic/Masquerade/Masquerade.storage';

import {getRequestAgent} from './requestAgent';
import {getSession, setSessionResource} from './session';
import {setLoggingOut, clearLoggingOut} from './logoutState';

export {getRequestAgent, getSession};

/**
 * @todo This is probably too coupled to the authorization configuartion in the API
 *       it seems like a decent way to avoid this would be to provide a cookie explicitly
 *       for use by _this_ application.
 * @see  https://github.com/albert-io/mandark/pull/4711 for implementation (and coupling) context.
 */
const COOKIE_NAME = 'guardian_authorization_0_token';
const API_HOST = getHost();

/**
 * Primarily used by the Express server to extract the authentication cookie from
 * a HTTP request.
 *
 * @param {object} cookies The cookie object provided by ExpressJS
 * @returns {string|null} Returns the value of the matching authorization cookie or `null`
 */
export function getAuthenticationCookie(cookies) {
  return cookies && cookies[COOKIE_NAME] ? cookies[COOKIE_NAME] : null;
}

export function decode(token) {
  if (token) {
    try {
      return _decode(token);
    } catch (e) {
      notifier.notify(e, {
        message: 'Unable to decode JWT token',
        tags: 'authentication'
      });
    }
  }
  return {};
}

export function login(payload, initializeSession = true) {
  const r = getRequestAgent().post(`${API_HOST}/auth/sign_in`).send(payload);
  if (initializeSession) {
    return r.then(resumeSession);
  }
  return r;
}

export function logout(redirect = '/') {
  // Set before the POST so requests that 401 because the sign-out already landed
  // server-side are covered too.
  setLoggingOut();
  return getRequestAgent()
    .post(`${API_HOST}/auth/sign_out`)
    .then(() => closeSession(redirect))
    .catch((err) => {
      // Sign-out failed, so the page is staying put: un-latch, or every later navigation
      // in this document is suppressed. Rethrow to leave the caller's handling unchanged.
      clearLoggingOut();
      throw err;
    });
}

export function resumeSession() {
  return getSessionDetails()
    .then((res) => {
      if (res.status === 200 && res.body) {
        return bootstrapSessionFromResponse(res);
      }
      return Promise.resolve();
    })
    .catch(() => {
      return getRequestAgent().post(`${API_HOST}/auth/session/reset`);
    });
}

/**
 * @todo I want to figure out a better way to handle this... basically
 * this is used for re-fetching the current session information AND ensuring
 * a re-render by altering the state tree.
 */
export function refreshSession() {
  return resumeSession();
}

export function getSessionDetails() {
  return getRequestAgent().get(`${API_HOST}/auth/session`);
}

function bootstrapSessionFromResponse(response) {
  const {body} = response;
  if (!body) {
    throw Error('Unable to locate session details.');
  }
  const {user} = body;
  if (!user) {
    return;
  }

  setSessionResource(user);
  callAction(SessionLifecycleActions.SET_SESSION_STATE, getSession().isValid());

  IntercomService.bootIntercomUser({
    user: {
      id: getSession().getId()
    },
    session: {
      isSuper: getSession().isSuper(),
      isContentManager: getSession().isContentManager(),
      hasTeacherAccess: getSession().hasTeacherAccess()
    }
  });
}

function closeSession(redirect) {
  IntercomService.shutdownIntercom();

  // Do NOT dispatch session state here: the change event is deferred (lib/state.ts), so it
  // lands mid-reload and refires every stale mandark read with a dead cookie.

  // Masquerade is persisted in sessionStorage to survive refresh; drop it on
  // logout so it can't leak into a different account in the same tab.
  clearMasqueradeState();

  let cookieDomain = 'albert.io';
  if (isInternal()) {
    cookieDomain = getEnvironment();
  }

  Cookies.remove('lti_launch_token', {domain: cookieDomain});
  Cookies.remove('lti_launch_context', {domain: cookieDomain});

  /**
   * IMPORTANT**: In the browser we **MUST** do a hard refresh of the page otherwise we
   * could potentially leak application state between sessions (module-level Flux store
   * state and caches populated while authenticated).
   */
  global.document.location = redirect;
}
